import type { EmotionStyle } from '@hotjar/webapp/features/Feedback/domain/EmotionStyle';
import type { AbstractSingleFilterPillsProps } from '@hotjar/global-filters';
import { GenericShortListFilter } from '@hotjar/global-filters';
import { useEmotionLabels } from '@hotjar/webapp/features/Feedback/EmotionIcon';
import { useTranslation } from '@hotjar/translations';
import { areAllRatingsIncluded, ratingFilterOptions } from './RatingFilterType';
import type { RATING_TYPE, RatingFilterValue } from './RatingFilterType';

export interface RatingFilterProps extends AbstractSingleFilterPillsProps<RatingFilterValue> {
  emotionStyle: EmotionStyle;
}

export function RatingFilter({
  currentValue,
  onApply,
  onRemove,
  onDropdownHidden,
  showDropdownOnMount,
  emotionStyle,
  isInactive,
}: RatingFilterProps): JSX.Element {
  const emotionLabels = useEmotionLabels();
  const { t: tFilter } = useTranslation('feedback', {
    keyPrefix: 'feedback-responses.filters.has-rating',
  });
  const { t } = useTranslation('feedback', { keyPrefix: 'survey-response.filters.rating' });

  const options = ratingFilterOptions(emotionStyle).map((option) => ({
    ...option,
    label: emotionLabels[option.value],
  }));

  function getSelectedOptionsLabel(ratings: RATING_TYPE[]): string {
    if (areAllRatingsIncluded(ratings)) {
      return tFilter('rating');
    }
    if (ratings.length > 2) {
      return `(${ratings.length})`;
    }
    return `${t('is')} ${ratings.map((rating) => emotionLabels[rating]).join(` ${t('or')} `)}`;
  }
  return (
    <GenericShortListFilter
      filterName={tFilter('rating')}
      iconName="like"
      currentValue={currentValue}
      onApply={onApply}
      onRemove={onRemove}
      optionList={options}
      label1={currentValue && areAllRatingsIncluded(currentValue) ? '' : tFilter('rating')}
      label2={currentValue ? getSelectedOptionsLabel(currentValue) : tFilter('rating')}
      onDropdownHidden={onDropdownHidden}
      showDropdownOnMount={showDropdownOnMount}
      showDropdownCaret
      isInactive={isInactive}
      selectAllLabel={tFilter('options.all')}
    />
  );
}
